// Aba CONFIGURAÇÃO — vínculo do modem e tradução dos canais, pela tela.
//
// Antes isto vivia em dois lugares fora do alcance de quem opera: o vínculo
// thing→estação era um INSERT via bastion, e o de-para canal→grandeza era um
// objeto literal dentro da Lambda de ingestão — estação nova exigia editar
// código e redeployar. É o que travava a escala (1 estação no IoT Core).
//
// O modem publica o valor com nome de fábrica (cha1, chi1). Quem sabe que
// "cha1 desta estação é Nível em metros" é o sistema, e agora isso é cadastro.
//
// 2026-08-09 — o nome da variável deixou de ser texto livre. Digitar aqui foi o
// que produziu `Nivel` na telemetria e `nivel` na leitura manual (duas séries
// para a mesma coisa) e um `NivelBat` que ninguém sabe o que é. Agora escolhe-se
// do catálogo, agrupado por grandeza física; nome que falta se CADASTRA, com
// grandeza, unidade e faixa declaradas.
import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Button, Input, InputNumber, Select, Space, Table, Tag, Alert,
  Typography, Popconfirm, Spin, Tooltip, Switch, Card,
} from 'antd';
import {
  SaveOutlined, PlusOutlined, DeleteOutlined, CopyOutlined,
  ThunderboltOutlined, CheckCircleFilled, WarningFilled, QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, Estacao, motivoModem } from './shared';
import { ModalVariavelNova, Grandeza, Variavel } from './ModalVariavelNova';
import { SecaoAlertas, Limiar, Destinatario, LimiarSugerido } from './SecaoAlertas';

const { Text, Paragraph } = Typography;

type Amostra = { lido_em: string; valor: number };
type Sugestao = {
  estado: 'sugere' | 'ambiguo' | 'nenhum_candidato' | 'sem_amostra' | 'sem_modelo_na_familia';
  candidatos: { modelo: string; rotulo: string; variavel: string | null }[];
  motivo?: { familia: string | null; amostras_n: number; min: number; max: number; descartados_pelo_passo: string[] };
};
type Modelo = {
  codigo: string; rotulo: string; grandeza_codigo: string; variavel_sugerida: string | null;
  familia_canal: string | null; faixa_min: number; faixa_max: number; resolucao: number | null;
  fabricante: string | null; observacao: string | null;
};
type Canal = {
  canal: string; variavel: string | null;
  valor_min: number | null; valor_max: number | null;
  ativo: boolean; visto_em: string | null; observacao: string | null;
  modelo_sensor: string | null; resolucao: number | null;
  confirmado_em: string | null; confirmado_por: string | null;
  // vindos do catálogo, para a conferência de compatibilidade
  grandeza_codigo?: string | null; variavel_rotulo?: string | null; unidade?: string | null;
  faixa_min?: number | null; faixa_max?: number | null; diagnostico?: boolean;
  variavel_status?: string | null;
  recentes_n?: number | null; recentes_fora?: number | null;
  recentes_min?: number | null; recentes_max?: number | null; recentes_ultimo_em?: string | null;
  amostras?: Amostra[];
  sugestao?: Sugestao | null;
  _novo?: boolean;
};

// Família do canal pelo NOME: chi = medição interna do equipamento, cha =
// analógico externo, chd = contagem de pulso. Restringe os modelos oferecidos —
// nunca decide a grandeza. `chi1` não é "sempre bateria": o Tipo de Entrada é
// configurável por canal (manual NOVUS, p.47).
function familiaCanal(canal: string) {
  const m = String(canal || '').toLowerCase().match(/^(chi|cha|chd)\d*$/);
  return m ? m[1] : null;
}

const fmt = (v: number | null | undefined, casas = 2) =>
  v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: casas });

// Minigráfico sem biblioteca: são 20 pontos num espaço de 90×22 px. Puxar
// recharts para isto custaria mais do que o desenho inteiro.
function Sparkline({ pontos }: { pontos: number[] }) {
  if (pontos.length < 2) return null;
  const min = Math.min(...pontos), max = Math.max(...pontos);
  const amp = max - min || 1;
  const L = 92, A = 22;
  const d = pontos.map((v, i) =>
    `${(i / (pontos.length - 1)) * L},${A - ((v - min) / amp) * (A - 3) - 1.5}`).join(' ');
  return (
    <svg width={L} height={A} style={{ verticalAlign: 'middle' }}>
      <polyline points={d} fill="none" stroke="#1677ff" strokeWidth={1.4} strokeLinejoin="round" />
    </svg>
  );
}

export function TabConfigTelemetria({ est, podeConfigurar, podeGerenciarVariaveis, transmissao }: {
  est: Estacao; podeConfigurar: boolean; podeGerenciarVariaveis?: boolean;
  // O meio muda o que há pra configurar: só o IoT Core tem thing name e
  // certificado nossos. Satélite (Orbcomm) e API de parceiro entram por outro
  // caminho — mostrar o roteiro do IoT ali seria instrução errada.
  transmissao?: 'iot' | 'satelital' | 'api' | null;
}) {
  const { message } = AntApp.useApp();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [station, setStation] = useState('');
  const [canais, setCanais] = useState<Canal[]>([]);
  const [grandezas, setGrandezas] = useState<Grandeza[]>([]);
  const [variaveis, setVariaveis] = useState<Variavel[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [conexao, setConexao] = useState<any>(null);
  const [modalVar, setModalVar] = useState(false);
  // Quais canais estavam pendentes QUANDO A TELA CARREGOU. Sem isto o cartão
  // some no instante em que se aceita a sugestão — e sumir parece "pronto",
  // quando na verdade ainda falta salvar. O cartão fica e passa a cobrar.
  const [pendentesNoLoad, setPendentesNoLoad] = useState<string[]>([]);
  const [alertas, setAlertas] = useState<{ limiares: Limiar[]; destinatarios: Destinatario[]; sugeridos: LimiarSugerido[] }>(
    { limiares: [], destinatarios: [], sugeridos: [] });

  async function carregar() {
    setLoading(true);
    try {
      const r = await api('op_estacao_telemetria_config', { id: est.id });
      const iot = (r.vinculos || []).find((v: any) => v.client === 'IoTCore');
      setStation(iot?.station || '');
      setCanais(r.canais || []);
      setPendentesNoLoad((r.canais || []).filter((c: Canal) => !c.variavel && c.visto_em).map((c: Canal) => c.canal));
      setGrandezas(r.grandezas || []);
      setVariaveis(r.variaveis || []);
      setModelos(r.modelos || []);
      setConexao(r.conexao || null);
      setAlertas({ limiares: r.limiares || [], destinatarios: r.destinatarios || [],
                   sugeridos: r.limiares_sugeridos || [] });
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [est.id]);

  const set = (i: number, patch: Partial<Canal>) =>
    setCanais(cs => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const varPorCodigo = useMemo(() => {
    const m = new Map<string, Variavel>();
    variaveis.forEach(v => m.set(v.codigo, v));
    return m;
  }, [variaveis]);

  // Select agrupado por grandeza: é a grandeza que torna estações comparáveis,
  // então ela é o que organiza a escolha. Diagnóstico vai num grupo à parte —
  // são as que nunca devem virar série do cliente.
  const opcoesVariavel = useMemo(() => {
    const porG = new Map<string, Variavel[]>();
    for (const v of variaveis) {
      const chave = v.diagnostico ? '__diag' : v.grandeza_codigo;
      if (!porG.has(chave)) porG.set(chave, []);
      porG.get(chave)!.push(v);
    }
    const rotuloG = (c: string) =>
      c === '__diag' ? 'Diagnóstico do equipamento (não vai para o cliente)'
        : (grandezas.find(g => g.codigo === c)?.rotulo || c);
    return Array.from(porG.entries())
      .sort((a, b) => (a[0] === '__diag' ? 1 : b[0] === '__diag' ? -1 : 0))
      .map(([g, vs]) => ({
        label: rotuloG(g),
        options: vs.map(v => ({
          value: v.codigo,
          label: `${v.codigo}${v.unidade ? ` (${v.unidade})` : ''}${v.status === 'proposta' ? ' · proposta' : ''}`,
          title: `${v.rotulo}${v.faixa_min != null ? ` — faixa ${fmt(v.faixa_min)} a ${fmt(v.faixa_max)}` : ''}`,
        })),
      }));
  }, [variaveis, grandezas]);

  // Escolher o modelo de sensor é o atalho: ele carrega a faixa e o passo. É a
  // resposta ao "sonda de 0-5 ou de 0-10 mca?" — a faixa é propriedade do
  // MODELO, não da grandeza nem da instalação.
  function aplicarModelo(i: number, codigo: string | null) {
    const m = modelos.find(x => x.codigo === codigo);
    if (!m) { set(i, { modelo_sensor: null }); return; }
    const variavel = canais[i]?.variavel || m.variavel_sugerida || null;
    set(i, {
      modelo_sensor: m.codigo,
      valor_min: Number(m.faixa_min), valor_max: Number(m.faixa_max),
      resolucao: m.resolucao == null ? null : Number(m.resolucao),
      variavel,
      // Canal detectado pela ingestao nasce ativo = FALSE, e a ingestao so le
      // `WHERE ativo = TRUE`. Sem ligar aqui, o operador escolhe a variavel,
      // salva, ve "configurado" e o dado continua nao entrando na serie.
      ...(variavel ? { ativo: true } : {}),
    });
  }

  function aceitarSugestao(i: number) {
    const c = canais[i];
    const cand = c.sugestao?.candidatos?.[0];
    if (cand) aplicarModelo(i, cand.modelo);
  }

  async function salvar() {
    if (canais.some(c => !String(c.canal || '').trim())) { message.warning('Todo canal precisa de nome (ex.: cha1).'); return; }
    setSalvando(true);
    try {
      const r = await api('op_estacao_telemetria_config_salvar', {
        id: est.id, station: station.trim(), canais,
      });
      // O reprocessamento é a parte que o operador não veria sozinho: o valor
      // que chegou antes de alguém mapear o canal estava só no buffer.
      if (r?.reprocessadas > 0) {
        message.success(`Configuração salva. ${r.reprocessadas} leitura(s) que estavam represadas entraram na série.`);
      } else {
        message.success(r?.aviso_cache || 'Configuração salva.');
      }
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  async function excluir(canal: string) {
    setSalvando(true);
    try {
      await api('op_estacao_telemetria_config_salvar', { id: est.id, canais: [{ canal, excluir: true }] });
      message.success('Canal removido.');
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  const roteiro = `# Provisionar este modem no AWS IoT Core (rodar uma vez, no escritório)
NOME=${station || '<ID-DO-MODEM>'}     # ATÉ 20 CARACTERES — o NXperience corta o resto
SERIAL=<n-de-serie-do-LogBox>

aws iot create-thing --thing-name $NOME --thing-type-name estacao-hidrologica \\
  --attribute-payload "attributes={serial=$SERIAL}" --region us-east-1
aws iot create-keys-and-certificate --set-as-active \\
  --certificate-pem-outfile cert.pem --public-key-outfile pub.key \\
  --private-key-outfile priv.key --region us-east-1 > cert-arn.txt
aws iot attach-policy --policy-name estacao-telemetria --target "$(grep -o 'arn:[^"]*' cert-arn.txt | head -1)"
aws iot attach-thing-principal --thing-name $NOME --principal "$(grep -o 'arn:[^"]*' cert-arn.txt | head -1)"
curl -o AmazonRootCA1.pem https://www.amazontrust.com/repository/AmazonRootCA1.pem

# No NXperience (cabo USB — o certificado NÃO entra remotamente):
#   Segurança .... TLS V1.2 - Self Signed   (é assim que a NOVUS chama mTLS;
#                                            "CA Only" falha mostrando só "MQTT Err")
#   Tópico ....... hydrodata/estacoes/$NOME/records
#   Canais ....... habilitar TAMBÉM os internos de diagnóstico
#                  (Tensão da bateria e Tensão da fonte externa)`;

  const colunas = [
    {
      title: 'Canal', dataIndex: 'canal', width: 110,
      render: (v: string, _: Canal, i: number) => (
        <Input size="small" value={v} disabled={!podeConfigurar}
          onChange={e => set(i, { canal: e.target.value.trim() })} placeholder="cha1" />
      ),
    },
    {
      title: 'Modelo do sensor', dataIndex: 'modelo_sensor', width: 210,
      render: (v: string | null, r: Canal, i: number) => {
        const fam = familiaCanal(r.canal);
        const opts = modelos.filter(m => !fam || !m.familia_canal || m.familia_canal === fam);
        return (
          <Select size="small" style={{ width: '100%' }} value={v || undefined} allowClear showSearch
            disabled={!podeConfigurar} placeholder="opcional — preenche a faixa"
            optionFilterProp="label"
            options={opts.map(m => ({ value: m.codigo, label: m.rotulo }))}
            onChange={(val) => aplicarModelo(i, val || null)} />
        );
      },
    },
    {
      title: 'Vira a variável', dataIndex: 'variavel', width: 230,
      render: (v: string | null, r: Canal, i: number) => {
        const cat = v ? varPorCodigo.get(v) : undefined;
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Select size="small" style={{ width: '100%' }} value={v || undefined} allowClear
              disabled={!podeConfigurar} placeholder={r.visto_em ? 'configure para gravar' : 'ignorar'}
              showSearch optionFilterProp="label" options={opcoesVariavel}
              onChange={(val) => {
                const nova = val || null;
                const c = nova ? varPorCodigo.get(nova) : undefined;
                // Sem modelo de sensor escolhido, a faixa da própria variável é
                // o padrão razoável — evita salvar canal sem nenhum limite.
                set(i, {
                  variavel: nova,
                  // Escolher a variavel liga o canal (ver aplicarModelo).
                  ...(nova ? { ativo: true } : {}),
                  ...(c && !r.modelo_sensor && r.valor_min == null && r.valor_max == null
                    ? { valor_min: c.faixa_min, valor_max: c.faixa_max, resolucao: c.resolucao }
                    : {}),
                });
              }} />
            {cat && (
              <Text type="secondary" style={{ fontSize: 10 }}>
                {cat.rotulo}
                {cat.faixa_min != null && ` · ${fmt(cat.faixa_min)} a ${fmt(cat.faixa_max)} ${cat.unidade}`}
                {cat.diagnostico && ' · diagnóstico'}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: <Tooltip title="Fora desta faixa a leitura é descartada — protege de cabo solto e sonda seca">Faixa válida</Tooltip>,
      width: 180,
      render: (_: any, r: Canal, i: number) => (
        <Space size={4}>
          <InputNumber size="small" decimalSeparator="," style={{ width: 82 }} value={r.valor_min ?? undefined}
            disabled={!podeConfigurar} placeholder="mín" step={0.1}
            onChange={v => set(i, { valor_min: v as number })} />
          <InputNumber size="small" decimalSeparator="," style={{ width: 82 }} value={r.valor_max ?? undefined}
            disabled={!podeConfigurar} placeholder="máx" step={0.1}
            onChange={v => set(i, { valor_max: v as number })} />
        </Space>
      ),
    },
    {
      title: 'Ativo', dataIndex: 'ativo', width: 62, align: 'center' as const,
      render: (v: boolean, _: Canal, i: number) => (
        <Switch size="small" checked={v} disabled={!podeConfigurar} onChange={c => set(i, { ativo: c })} />
      ),
    },
    {
      // Responde "o dado que está chegando bate com o que eu disse que é?" sem
      // o operador precisar abrir o gráfico e conferir a olho.
      title: <Tooltip title="Confere as leituras das últimas 24 h contra a faixa declarada da variável">Dado compatível?</Tooltip>,
      width: 200,
      render: (_: any, r: Canal) => {
        if (!r.variavel) return r.visto_em ? <Tag color="orange">chegando · não configurado</Tag> : <Tag>ignorado</Tag>;
        if (!r.ativo) return <Tag color="default">desligado</Tag>;
        if (!r.recentes_n) return <Text type="secondary" style={{ fontSize: 11 }}>sem leitura nas últimas 24 h</Text>;
        const fora = Number(r.recentes_fora || 0);
        const faixa = `${fmt(r.recentes_min)} a ${fmt(r.recentes_max)}`;
        if (fora > 0) return (
          <Tooltip title={`Faixa declarada: ${fmt(r.faixa_min)} a ${fmt(r.faixa_max)}. Observado: ${faixa}.`}>
            <Tag icon={<WarningFilled />} color="red">{fora} de {r.recentes_n} fora da faixa</Tag>
          </Tooltip>
        );
        return (
          <Tooltip title={`${r.recentes_n} leituras em 24 h, todas dentro de ${fmt(r.faixa_min)} a ${fmt(r.faixa_max)}.`}>
            <Tag icon={<CheckCircleFilled />} color="green">compatível · {faixa}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Visto', dataIndex: 'visto_em', width: 110,
      render: (v: string | null) => v
        ? <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(v).format('DD/MM HH:mm')}</Text>
        : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    ...(podeConfigurar ? [{
      title: '', width: 46,
      render: (_: any, r: Canal) => (
        <Popconfirm title="Remover este canal da configuração?" onConfirm={() => excluir(r.canal)}>
          <Button size="small" danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

  const pendentes = canais
    .map((c, i) => ({ c, i }))
    .filter(x => pendentesNoLoad.includes(x.c.canal));
  const aindaSemSalvar = pendentes.filter(x => x.c.variavel).length;

  return (
    <>
      {conexao && (
        <Alert showIcon style={{ marginBottom: 10 }}
          type={conexao.conectado ? 'success' : 'warning'}
          message={conexao.conectado
            ? `Modem conectado desde ${dayjs(conexao.mudou_em).format('DD/MM/YYYY HH:mm')}`
            : `Modem desconectado desde ${dayjs(conexao.mudou_em).format('DD/MM/YYYY HH:mm')}`
              + (motivoModem(conexao.motivo) ? ` — ${motivoModem(conexao.motivo)}` : '')} />
      )}

      {/* Canal chegando sem tradução: mostra o VALOR, não só o nome. Sem o valor
          o operador tem que adivinhar qual grandeza é — com ele, o sistema
          sugere e ele confirma. */}
      {pendentes.length > 0 && (
        <Card size="small" style={{ marginBottom: 12, borderColor: '#ffd591', background: '#fffbe6' }}
          styles={{ body: { padding: 12 } }}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Space wrap>
              <Text strong style={{ fontSize: 13 }}>
                {pendentes.length} canal(is) chegando sem tradução — o valor está sendo guardado, mas ainda não entra na série
              </Text>
              {aindaSemSalvar > 0 && (
                <Tag color="gold">{aindaSemSalvar} escolhido(s) — falta clicar em <b>Salvar configuração</b></Tag>
              )}
            </Space>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
              {pendentes.map(({ c, i }) => {
                const am = c.amostras || [];
                const vals = am.map(a => Number(a.valor)).filter(Number.isFinite);
                const s = c.sugestao;
                const cand = s?.candidatos || [];
                return (
                  <Card key={c.canal} size="small" style={{ background: '#fff' }} styles={{ body: { padding: 10 } }}>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space size={6}>
                        <Tag color="orange" style={{ margin: 0, fontFamily: 'monospace' }}>{c.canal}</Tag>
                        {familiaCanal(c.canal) === 'chi' && <Text type="secondary" style={{ fontSize: 10 }}>medição interna</Text>}
                        {familiaCanal(c.canal) === 'cha' && <Text type="secondary" style={{ fontSize: 10 }}>sensor analógico</Text>}
                        {familiaCanal(c.canal) === 'chd' && <Text type="secondary" style={{ fontSize: 10 }}>contagem de pulso</Text>}
                      </Space>
                      <Sparkline pontos={vals.slice(0, 20).reverse()} />
                    </Space>

                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      {vals.length ? (
                        <>
                          <b style={{ fontSize: 15 }}>{fmt(vals[0], 3)}</b>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {' '}último · {vals.length} amostras · {fmt(Math.min(...vals), 3)} a {fmt(Math.max(...vals), 3)}
                          </Text>
                        </>
                      ) : <Text type="secondary" style={{ fontSize: 11 }}>ainda sem valor guardado</Text>}
                    </div>

                    {c.variavel && (
                      <Alert type="info" showIcon style={{ marginTop: 8 }}
                        message={<span style={{ fontSize: 12 }}>Vai virar <b>{c.variavel}</b></span>}
                        description={<Text type="secondary" style={{ fontSize: 11 }}>
                          Ainda não está valendo — clique em <b>Salvar configuração</b>. Ao salvar, as {vals.length} leituras
                          guardadas entram na série.
                        </Text>} />
                    )}

                    {/* O porquê vem junto com o palpite. Sugestão sem fundamento
                        é caixa-preta: o operador confirma no automático e o erro
                        entra na série com carimbo de conferido. */}
                    {!c.variavel && s?.estado === 'sugere' && (
                      <Alert type="success" showIcon style={{ marginTop: 8 }} icon={<ThunderboltOutlined />}
                        message={<span style={{ fontSize: 12 }}>Provavelmente <b>{cand[0].rotulo}</b></span>}
                        description={
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            É o único sensor desta família cuja faixa e cujo passo aceitam todos os {s.motivo?.amostras_n} valores.
                            {s.motivo?.descartados_pelo_passo?.length
                              ? ` Descartados pelo passo: ${s.motivo.descartados_pelo_passo.join(', ')}.`
                              : ''}
                          </Text>}
                        action={podeConfigurar
                          ? <Button size="small" type="primary" onClick={() => aceitarSugestao(i)}>Usar</Button>
                          : undefined} />
                    )}
                    {!c.variavel && s?.estado === 'ambiguo' && (
                      <Alert type="warning" showIcon style={{ marginTop: 8 }} icon={<QuestionCircleOutlined />}
                        message={<span style={{ fontSize: 12 }}>Não dá para decidir pelo valor</span>}
                        description={
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Os valores cabem em {cand.map(x => x.rotulo).join(' e ')}. Escolha na tabela abaixo quem sabe o que foi habilitado no equipamento.
                          </Text>} />
                    )}
                    {!c.variavel && s?.estado === 'nenhum_candidato' && (
                      <Alert type="error" showIcon style={{ marginTop: 8 }}
                        message={<span style={{ fontSize: 12 }}>Nenhum sensor conhecido produz esses valores</span>}
                        description={
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Observado {fmt(s.motivo?.min, 3)} a {fmt(s.motivo?.max, 3)}. Pode ser sensor novo (cadastre a variável)
                            ou decodificação errada no equipamento.
                          </Text>} />
                    )}
                  </Card>
                );
              })}
            </div>
          </Space>
        </Card>
      )}

      <Space style={{ marginBottom: 10 }} wrap>
        <Text strong style={{ fontSize: 13 }}>
          {transmissao === 'satelital' ? 'MobileID do terminal'
            : transmissao === 'api' ? 'Identificador enviado pelo parceiro'
            : 'Identificador do modem'}
        </Text>
        <Tooltip title="É o nome da 'thing' no AWS IoT Core e o clientId que o LogBox usa para conectar. O NXperience corta em 20 caracteres.">
          <Input size="small" style={{ width: 260 }} value={station} disabled={!podeConfigurar}
            onChange={e => setStation(e.target.value)} placeholder="TELE-EXEMPLO-01" />
        </Tooltip>
        {transmissao === 'iot' && station.length > 20 && <Tag color="red">acima de 20 caracteres</Tag>}
      </Space>

      <Table size="small" rowKey="canal" dataSource={canais} columns={colunas as any}
        pagination={false} style={{ marginBottom: 10 }} scroll={{ x: 1150 }}
        locale={{ emptyText: 'Nenhum canal configurado. Assim que o modem transmitir, os canais aparecem aqui sozinhos.' }} />

      {podeConfigurar && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Button size="small" icon={<PlusOutlined />}
            onClick={() => setCanais(cs => [...cs, {
              canal: '', variavel: null, valor_min: null, valor_max: null, ativo: true,
              visto_em: null, observacao: null, modelo_sensor: null, resolucao: null,
              confirmado_em: null, confirmado_por: null, _novo: true,
            }])}>
            Adicionar canal
          </Button>
          {podeGerenciarVariaveis && (
            <Tooltip title="Só quando nenhuma variável do catálogo serve. Nome novo exige grandeza, unidade e faixa — é o que impede a base voltar a aceitar 374° de direção de vento.">
              <Button size="small" onClick={() => setModalVar(true)}>Cadastrar variável nova</Button>
            </Tooltip>
          )}
          <Button size="small" type="primary" icon={<SaveOutlined />} loading={salvando} onClick={salvar}>
            Salvar configuração
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>A ingestão aplica em até 60 segundos.</Text>
        </Space>
      )}

      {/* Alertas: mora aqui porque quem mapeia o canal e quem sabe o que e
          valor normal para ele. */}
      <SecaoAlertas estacaoId={est.id} variaveis={variaveis}
        limiares={alertas.limiares} destinatarios={alertas.destinatarios}
        sugeridos={alertas.sugeridos} podeConfigurar={podeConfigurar}
        onSalvo={carregar} />

      <ModalVariavelNova aberto={modalVar} grandezas={grandezas} variaveis={variaveis}
        onFechar={() => setModalVar(false)}
        onCriada={(v) => {
          setVariaveis(vs => [...vs, v]);
          setModalVar(false);
        }} />

      {transmissao && transmissao !== 'iot' && (
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message={transmissao === 'satelital'
            ? 'Estação satelital (Orbcomm)'
            : 'Telemetria por API de parceiro'}
          description={transmissao === 'satelital'
            ? 'O dado chega pelo poller de satélite, não pelo IoT Core: o vínculo é o MobileID do terminal, e não há certificado nosso pra provisionar. O mapeamento de canais abaixo continua valendo.'
            : 'O parceiro empurra os dados por HTTP com token (X-Telemetry-Token) — não há modem nosso nem certificado a provisionar. O vínculo é o identificador que o parceiro envia, e o mapeamento de canais abaixo continua valendo.'} />
      )}
      {transmissao === 'iot' && (
      <details style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, background: '#fafafa' }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          Roteiro de provisionamento (certificado — feito uma vez por modem)
        </summary>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 6 }}>
          O certificado do IoT Core <b>só entra por cabo USB</b> — não há provisionamento remoto.
          Este roteiro cria a identidade do modem na AWS; a gravação no equipamento é feita no NXperience.
        </Paragraph>
        <pre style={{ fontSize: 11, background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 8, overflow: 'auto', maxHeight: 260 }}>{roteiro}</pre>
        <Button size="small" icon={<CopyOutlined />} onClick={() => {
          navigator.clipboard?.writeText(roteiro); message.success('Roteiro copiado.');
        }}>Copiar</Button>
      </details>
      )}
    </>
  );
}
